import { Capacitor } from '@capacitor/core';
import axios from 'axios';
import { defer, map } from 'rxjs';
import { ADVANCED_QUESTION_TYPES } from '../../../server/src/common/utils/constants';

export const getPlatform = () => {
  return Capacitor.getPlatform();
};

export const isEmpty = (obj) => {
  for (let i in obj) return false;
  return true;
};

export const arrayToEntity = (arr) => {
  const result = arr.reduce((acc, val) => {
    acc[val.id] = val;
    return acc;
  }, {});
  return result;
};

/**
 * Inserts a new item into an array at the specified index.
 *
 * @param {Array} arr - The original array.
 * @param {number} index - The index at which to insert the new item.
 * @param {Array} newItem - The new item to insert.
 * @returns {Array} - The updated array with the new item inserted.
 */
export const insert = (arr, index, newItem) => [
  ...arr.slice(0, index),
  ...newItem,
  ...arr.slice(index),
];

export const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

export const scrollEnd = (el) => {
  if (!el) return;
  const parentEl = el?.parentElement;
  if (parentEl) {
    setTimeout(() => {
      parentEl.scrollTo(0, parentEl.scrollHeight);
    });
  }
};

export const generateThumbnail = async (url, size) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    canvas.style.cssText = `position: fixed; left: 0; top: 0; width: ${size}px; height: ${size}px; z-index: 9999; display: none;`;
    document.body.appendChild(canvas);
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      const { width, height } = img;

      let drawWidth = size;
      let drawHeight = size;
      let offsetX = 0;
      let offsetY = 0;

      if (width < height) {
        drawHeight = height * (size / width);
        offsetY = -(drawHeight - drawWidth) / 2;
      } else {
        drawWidth = width * (size / height);
        offsetX = -(drawWidth - drawHeight) / 2;
      }

      ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
      const dataURL = canvas.toDataURL();
      document.body.removeChild(canvas);

      resolve(dataURL);
    };
    img.src = url;
    img.crossOrigin = 'Anonymous';
  });
};

export const isValidUrl = (str) => {
  const urlRegex =
    '^(?!mailto:)(?:(?:http|https|ftp)://)(?:\\S+(?::\\S*)?@)?(?:(?:(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}(?:\\.(?:[0-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))|(?:(?:[a-z\\u00a1-\\uffff0-9]+-?)*[a-z\\u00a1-\\uffff0-9]+)(?:\\.(?:[a-z\\u00a1-\\uffff0-9]+-?)*[a-z\\u00a1-\\uffff0-9]+)*(?:\\.(?:[a-z\\u00a1-\\uffff]{2,})))|localhost)(?::\\d{2,5})?(?:(/|\\?|#)[^\\s]*)?$';
  const url = new RegExp(urlRegex, 'i');
  return str.length < 2083 && url.test(str);
};

export const isValidHostName = (str) => {
  const hostNameRegex = /^(?!:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]+|^$/i;
  return hostNameRegex.test(str);
};

export const isValidSubHostName = (str) => {
  const hostNameRegex = /^(?!:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]+|^$/i;
  return hostNameRegex.test(str);
};

export const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

export const get = (url, queryParams) => {
  return defer(() => axios.get(url, { params: queryParams })).pipe(map((result) => result.data));
};

export const queryString = (string) => {
  return string
    .slice(1)
    .split('&')
    .map((queryParam) => {
      let data = queryParam.split('=');
      return { key: data[0], value: data[1] };
    })
    .reduce((query, data) => {
      query[data.key] = data.value;
      return query;
    }, {});
};

export const uploadImageToS3 = async (
  fileName,
  setUploading,
  { file, onError, onProgress, onSuccess },
) => {
  setUploading(true);
  const bucketName = 'uploads.whatsform.com';
  const uploadUrl = await axios
    .get(
      `/api/generate-pre-signedurl?key=${fileName}&fileType=${file.type}&bucketName=${bucketName}`,
    )
    .then((res) => res.data.url);
  const options = {
    onUploadProgress: (event) => {
      const { loaded, total } = event;
      onProgress(
        {
          percent: Math.round((loaded / total) * 100),
        },
        file,
      );
    },
    headers: {
      'Content-Type': file.type,
    },
  };
  const axiosInstance = axios.create();
  //
  const result = await axiosInstance.put(uploadUrl, file, options).catch((error) => {
    console.log(error.message);
    setUploading(false);
    onError(error);
  });
  onSuccess(result, file);
  setUploading(false);
  return {
    type: file.type,
    url:
      uploadUrl.replace(/s3\.(?:\S*\.)?amazonaws\.com\//, '').split('?')[0] + '?x-t=' + Date.now(),
    // Amazon will ignore query params that begin w/ x-
    // https://docs.aws.amazon.com/AmazonS3/latest/userguide/LogFormat.html
    name: file.name,
  };
};

export const checkForAdvancedFields = (formQuestions) => {
  const [SECTION, ...ADVANCED_TYPES] = ADVANCED_QUESTION_TYPES;
  const hasMoreThanOneSection =
    formQuestions?.filter((question) => question.type === SECTION)?.length > 1;
  return (
    hasMoreThanOneSection ||
    formQuestions?.some((question) => ADVANCED_TYPES.includes(question.type))
  );
};

export const getRootDomain = (customDomain) => {
  let domain = 'https://whatsform.com';
  if (window.location.href.includes('staging.whatsform.com')) {
    domain = 'https://staging.whatsform.com';
  }
  if (customDomain && customDomain.hostname) {
    domain = `https://${customDomain.hostname}`;
  }
  return domain;
};

export const getFormLink = (customDomain, link, customDomainLink) => {
  let domain = getRootDomain(customDomain);
  let formLink = link;
  if (customDomain && customDomain.hostname) {
    formLink = customDomainLink ? customDomainLink : link;
  }
  return `${domain}/${formLink}`;
};
